import { supabase } from '@/core/database/supabase';
import type { Project, ProjectStats, ProjectFilters } from '../types/projects.types';

export const ProjectsService = {
  /**
   * Lấy danh sách dự án với bộ lọc và phân trang
   */
  async getProjects(companyId: string, filters: ProjectFilters = {}): Promise<{ data: Project[], total: number }> {
    // Defensive check for companyId
    const validCompanyId = typeof companyId === 'string' ? companyId : null;

    let query = supabase
      .from('projects')
      .select(`
        *,
        pm:user_profiles!pm_user_id(id, full_name, avatar_url:avatar_path),
        site_manager:user_profiles!site_manager_user_id(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' });

    if (validCompanyId) {
      query = query.eq('company_id', validCompanyId);
    }
    
    query = query.is('deleted_at', null);

    // Apply Filters
    if (filters.search) {
      query = query.or(`name.ilike.%${filters.search}%,code.ilike.%${filters.search}%,client_name.ilike.%${filters.search}%`);
    }
    if (filters.status && filters.status !== 'all') {
      query = query.eq('status_code', filters.status);
    }
    if (filters.type && filters.type !== 'all') {
      query = query.eq('project_type', filters.type);
    }
    if (filters.pm_id) {
      query = query.eq('pm_user_id', filters.pm_id);
    }

    // Pagination
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as Project[],
      total: count || 0
    };
  },

  /**
   * Lấy chi tiết dự án
   */
  async getProjectDetail(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select(`
        *,
        pm:user_profiles!pm_user_id(id, full_name, avatar_url:avatar_path),
        site_manager:user_profiles!site_manager_user_id(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;

    const project = data as Project;
    
    // Extract thumbnail from description if thumbnail_url is missing (legacy support)
    if (!project.thumbnail_url && project.description) {
      const metadataMatch = project.description.match(/\[METADATA\]: (\{.*\})/);
      if (metadataMatch) {
        try {
          const metadata = JSON.parse(metadataMatch[1]);
          if (metadata.thumbnail) project.thumbnail_url = metadata.thumbnail;
        } catch (e) {}
      }
      
      if (!project.thumbnail_url) {
        const thumbMatch = project.description.match(/\[THUMBNAIL\]: (https?:\/\/\S+)/);
        if (thumbMatch) project.thumbnail_url = thumbMatch[1];
      }
    }

    return project;
  },

  /**
   * Tạo dự án mới
   */
  async createProject(data: Partial<Project>): Promise<Project> {
    const { data: result, error } = await supabase
      .from('projects')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as Project;
  },

  /**
   * Cập nhật dự án
   */
  async updateProject(id: string, updates: Partial<Project>): Promise<Project> {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Project;
  },

  /**
   * Xóa mềm dự án
   */
  async deleteProject(id: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Lấy thống kê dự án cho công ty
   */
  async getProjectStats(companyId: string): Promise<ProjectStats> {
    const { data, error } = await supabase
      .from('projects')
      .select('status_code, budget_amount, contract_amount')
      .eq('company_id', companyId)
      .is('deleted_at', null);

    if (error) throw error;

    const stats: ProjectStats = {
      total: data.length,
      active: data.filter(p => p.status_code === 'active').length,
      planning: data.filter(p => p.status_code === 'planning').length,
      completed: data.filter(p => p.status_code === 'completed').length,
      totalBudget: data.reduce((sum, p) => sum + (p.budget_amount || 0), 0),
      totalContract: data.reduce((sum, p) => sum + (p.contract_amount || 0), 0),
    };

    return stats;
  }
};
